import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { pickLocale } from "../../lib/format";
import AdfImage from "../storefront/AdfImage";

/**
 * The department panel.
 *
 * Kompakt panel (sahibin tapşırığı, 2026-10-05 — emporium.az nümunəsi): iki
 * hissə — solda YALNIZ əsas kateqoriyalar alt-alta + «Hamısına bax →», sağda
 * admin-in seçdiyi kampaniya şəkli və bir sətir mətn. Alt kateqoriyalar
 * paneldə deyil, kateqoriya səhifəsində başlığın altındadır. Admin bir
 * sütun qurubsa (Geyim → «Geyimlər» → kateqoriyalar) panel bu formadadır;
 * köhnə çox-sütunlu quruluş hələ də render olunur ki, data dəyişənə qədər
 * menyu boş qalmasın.
 *
 * `hidden` rather than conditional rendering — the node has to keep existing
 * for `aria-controls` to point at something, and `hidden` removes it from the
 * accessibility tree properly, which CSS `display: none` on a JS-controlled
 * panel would not.
 *
 * Moving between two entries that both open a panel keeps this one container
 * mounted and swaps its contents. Tearing it down and rebuilding it read as a
 * flash of the page between two menus, which is the tell of a cheap dropdown.
 */
export default function MegaMenuPanel({
  id,
  open,
  columns = [],
  feature = null,
  viewAll = null,
  onNavigate,
  panelRef,
}) {
  const { locale, t } = useI18n();
  const [entering, setEntering] = useState(true);
  const raf = useRef(0);
  const timer = useRef(0);

  // Mount hidden-and-offset, then release on the next frame so the transition
  // actually runs; going straight to the final state would show no animation.
  //
  // The timeout is not belt-and-braces: `requestAnimationFrame` does not fire
  // in a background tab, so a panel opened by keyboard in a tab the visitor
  // then switched away from would come back still at opacity 0 — open, laid
  // out and clickable, but invisible. Whichever fires first wins.
  useEffect(() => {
    if (!open) { setEntering(true); return undefined; }
    const reveal = () => setEntering(false);
    raf.current = requestAnimationFrame(reveal);
    timer.current = setTimeout(reveal, 60);
    return () => {
      cancelAnimationFrame(raf.current);
      clearTimeout(timer.current);
    };
  }, [open]);

  const featureTitle = pickLocale(feature?.title, locale);
  const featureText = pickLocale(feature?.text, locale);
  const featureCta = pickLocale(feature?.cta_label, locale);
  const hasFeature = Boolean(feature && (feature.image_url || featureTitle || featureText));
  const compact = columns.length === 1;

  const featureImage = feature?.image_url ? (
    <AdfImage
      source={feature.image_url}
      alt=""
      sizes="(max-width: 1440px) 60vw, 860px"
      className="adf-mega-feature__image"
    />
  ) : null;

  return (
    <div
      id={id}
      ref={panelRef}
      className="adf-mega-panel"
      hidden={!open}
      data-entering={entering ? "true" : "false"}
      data-testid="mega-menu-panel"
    >
      <div className="adf-container">
        <div
          className="adf-mega-body"
          data-compact={compact ? "true" : "false"}
          data-has-feature={hasFeature ? "true" : "false"}
        >
          {compact ? (
            <CompactColumn
              column={columns[0]}
              viewAll={viewAll}
              locale={locale}
              t={t}
              onNavigate={onNavigate}
            />
          ) : (
            <div className="adf-mega-grid" data-columns={Math.min(columns.length, 5)}>
              {columns.map((column) => {
                const heading = pickLocale(column.title, locale);
                return (
                  <div key={column.key} className="adf-mega-col">
                    {column.to ? (
                      <NavLink to={column.to} className="adf-mega-col-head" onClick={onNavigate}>
                        {heading}
                      </NavLink>
                    ) : (
                      <p className="adf-mega-col-head adf-mega-col-head--plain">{heading}</p>
                    )}
                    {column.links?.length > 0 && (
                      <ul className="adf-mega-list">
                        {column.links.map((child) => (
                          <li key={child.key}>
                            <NavLink to={child.to} className="adf-mega-link" onClick={onNavigate}>
                              {pickLocale(child.title, locale)}
                              {pickLocale(child.badge, locale) && (
                                <span className="adf-mega-badge">{pickLocale(child.badge, locale)}</span>
                              )}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {hasFeature && (
            <div className="adf-mega-feature" data-testid="mega-feature">
              {featureImage && (feature.cta_link ? (
                <NavLink to={feature.cta_link} className="adf-mega-feature__media" onClick={onNavigate} tabIndex={-1} aria-hidden="true">
                  {featureImage}
                </NavLink>
              ) : featureImage)}
              <div className="adf-mega-feature__body">
                {featureTitle && <p className="adf-mega-feature__title">{featureTitle}</p>}
                {featureText && <p className="adf-mega-feature__text">{featureText}</p>}
                {feature.cta_link && (featureCta || !featureImage) && (
                  <NavLink to={feature.cta_link} className="adf-mega-feature__cta" onClick={onNavigate}>
                    {featureCta || t("nav.view", "Bax")}
                    <ArrowRight size={13} strokeWidth={1.5} aria-hidden="true" />
                  </NavLink>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="adf-mega-footer">
          <NavLink
            to={viewAll || "/shop"}
            className="adf-mega-all"
            onClick={onNavigate}
            data-testid="mega-all-products"
          >
            {t("nav.all_products", "Bütün məhsullara bax")}
            <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />
          </NavLink>
        </div>
      </div>
    </div>
  );
}

/** Sol hissə: sütun başlığı (GEYİMLƏR), əsas kateqoriyalar alt-alta və sonda
 *  «Hamısına bax →» — sütunun öz ünvanına, yoxdursa bölmənin ünvanına. */
function CompactColumn({ column, viewAll, locale, t, onNavigate }) {
  const heading = pickLocale(column.title, locale);
  const allTo = column.to || viewAll;
  return (
    <div className="adf-mega-compact" data-testid="mega-compact">
      {heading && <p className="adf-mega-compact__head">{heading}</p>}
      <ul className="adf-mega-compact__list">
        {(column.links || []).map((child) => (
          <li key={child.key}>
            <NavLink to={child.to} className="adf-mega-compact__link" onClick={onNavigate}>
              {pickLocale(child.title, locale)}
              {pickLocale(child.badge, locale) && (
                <span className="adf-mega-badge">{pickLocale(child.badge, locale)}</span>
              )}
            </NavLink>
          </li>
        ))}
        {allTo && (
          <li>
            <NavLink to={allTo} className="adf-mega-compact__link adf-mega-compact__link--all" onClick={onNavigate}>
              {t("nav.view_all", "Hamısına bax")}
              <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />
            </NavLink>
          </li>
        )}
      </ul>
    </div>
  );
}
